import React, { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'todue-theme';

// Cross-domain theme share — cookie na parent `todue.sk` ide cez všetky subdomény
function cookieDomain() {
  if (typeof window === 'undefined') return null;
  const h = window.location.hostname;
  if (h === 'todue.sk' || h.endsWith('.todue.sk')) return 'todue.sk';
  return null;
}

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookie(name, value) {
  if (typeof document === 'undefined') return;
  const domain = cookieDomain();
  const base = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=31536000; SameSite=Lax`;
  document.cookie = domain ? `${base}; Domain=${domain}` : base;
}

function readStoredTheme() {
  try {
    const local = localStorage.getItem(STORAGE_KEY);
    if (local === 'dark' || local === 'light') return local;
    const cookied = readCookie(STORAGE_KEY);
    return cookied === 'dark' ? 'dark' : 'light';
  } catch (_e) { return 'light'; }
}

function writeStoredTheme(t) {
  try { localStorage.setItem(STORAGE_KEY, t); } catch (_e) {}
  writeCookie(STORAGE_KEY, t);
}

export function applyTheme(theme) {
  if (theme === 'dark') document.documentElement.setAttribute('data-theme', 'dark');
  else document.documentElement.removeAttribute('data-theme');
}

// Volaj v index.js pred React render aby si predišiel FOUC (flash of light theme)
export function initTheme() {
  applyTheme(readStoredTheme());
}

// Hook — kdekoľvek v app, drží source-of-truth v localStorage + html attr
export function useTheme() {
  const [theme, setTheme] = useState(readStoredTheme);
  useEffect(() => {
    applyTheme(theme);
    writeStoredTheme(theme);
  }, [theme]);
  const toggle = useCallback(() => {
    setTheme(t => t === 'dark' ? 'light' : 'dark');
  }, []);
  return { theme, setTheme, toggle };
}

export default function ThemeToggle({ size = 18, variant = 'plain' }) {
  const { theme, toggle } = useTheme();
  const next = theme === 'light' ? 'tmavý' : 'svetlý';

  const baseStyle = {
    width: 32, height: 32, padding: 0,
    background: variant === 'bordered' ? 'var(--bg-surface)' : 'transparent',
    border: variant === 'bordered' ? '1px solid var(--border)' : '1px solid transparent',
    borderRadius: 999, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--text-secondary)',
    transition: 'background var(--t-fast) var(--ease), color var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease)',
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={`Prepnúť na ${next} režim`}
      title={`Prepnúť na ${next} režim`}
      style={baseStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--bg-hover)';
        e.currentTarget.style.color = 'var(--text-primary)';
        e.currentTarget.style.borderColor = 'var(--border)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = variant === 'bordered' ? 'var(--bg-surface)' : 'transparent';
        e.currentTarget.style.color = 'var(--text-secondary)';
        e.currentTarget.style.borderColor = variant === 'bordered' ? 'var(--border)' : 'transparent';
      }}
    >
      {theme === 'dark' ? <SunIcon size={size} /> : <MoonIcon size={size} />}
    </button>
  );
}

function SunIcon({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
